import Badge from "../Badge";
import Button from "../Button";
import React, {useContext, useState} from "react";
import styled from "styled-components";
import {IProduct} from "../../interfaces/IProduct";
import Icon from "../Icon";
import AddToWishlist from "../wishlist/AddToWishlist";
import AddToCart from "../cart/addToCart";
import {useMessages} from "../../hooks/useMessages";
import SocialShare from "../SocialShare";
import NormalizedUnitPrice from "../NormalizedUnitPrice";
import Tag from "../Tag";
import {getTheme} from "../../shared/themeUtils";
import {ClientContext} from "../../contexts/ClientContext";
import parse from "html-react-parser";
import CustomTooltip from "../CustomTooltip";
import InternalPriceMargin from "./InternalPriceMargin";
import {getMappedStockColor} from "../../shared/stockColorMapUtil";
import * as vars from "../../shared/variables";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {request} from "../../shared/httpInterceptor";
import {useCountryLang} from "../../hooks/useCountryLang";
import {useAtom} from "jotai";
import {nextSessionAtom} from "../../store/sessionAtom";
import {Spinner} from "react-bootstrap";
import {useUserLoggedIn} from "../../hooks/useUserLoggedIn";

const CtaContain = styled.div`
  border: 1px solid ${getTheme("colors.border")};
  padding: ${getTheme("space.2")};
  border-radius: ${getTheme("borderRadius")};

    .stock-status {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .product-notification {
        display: flex;
        justify-content: center;
        margin-top: ${getTheme("space.0")};
        margin-bottom: ${getTheme("space.1")};

        button {
            display: flex;
            align-items: center;

            .notification-icon {
                margin-right: ${getTheme("space.0")};
            }
        }
    }

    .login-for-notification {
        text-align: center;
        color: ${getTheme("colors.secondaryFont")};
        font-size: ${getTheme("fontSizes.small")};
        margin-bottom: ${getTheme("space.1")};
    }

    .online-only {
        text-align: center;
        margin-bottom: ${getTheme("space.0")};
    }

    .shipping-text {
        text-align: center;
        font-size: ${getTheme("fontSizes.small")};
        color: ${getTheme("colors.secondaryFont")};
        margin-bottom: ${getTheme("space.1")};
    }
  
  .old-price {
    color: ${getTheme("colors.secondaryFont")};
  }

    .internal-margin {
        margin-top: ${getTheme("space.1")};
    }
  
  .share {
    margin-top: ${getTheme("space.2")};
    padding-top:  ${getTheme("space.2")};
    border-top: 1px solid ${getTheme("colors.border")};
  }
    
  .card-tag {
      position: relative;
      margin-left: ${getTheme("space.1")};
      top: -${getTheme("space.0")};
  }

    .product-in-cart {
        text-align: center;
        margin-top: ${getTheme("space.1")};
        position: relative;
        top: ${getTheme("space.1")};
        background: ${getTheme("colors.accentLight")};
        color: ${getTheme("colors.accent")};
        padding: ${getTheme("space.0")};
        border-radius: 20px;
        font-size: ${getTheme("fontSizes.small")};
    }

    .not-orderable {
        background: ${getTheme("colors.background")};
        margin: ${getTheme("space.1")} 0;
        padding: ${getTheme("space.1")};
        text-align: center;
        border-radius: ${getTheme("borderRadius")};
        font-size: ${getTheme("fontSizes.small")};
    }
    
    .not-returnable {
        color: ${getTheme("colors.secondaryFont")};
        text-align: center;
        font-size: ${getTheme("fontSizes.xSmall")};
    }
`

const ShippingCosts = styled.div`
  text-align: center;

  span.shipping-cost {
    text-align: center;
      font-size: ${getTheme("fontSizes.small")};
  }
`

interface ProductCTAProps {
  product: IProduct,
  productInCart?: boolean
}

const ProductCTA = (props: ProductCTAProps) => {
  const {product} = props;
  const [showShare, setShowShare] = useState(false);
  const messagesQuery = useMessages();
  const {clientCtx} = useContext(ClientContext);
  const countryLang = useCountryLang();
  const queryClient = useQueryClient();
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [sessionAtom] = useAtom(nextSessionAtom);
  const {session} = sessionAtom;
  const isUserLoggedIn = useUserLoggedIn();

  const handleShowShare = () => {
    setShowShare(!showShare)
  }

  // Check if there's an existing notification
  const hasExistingNotification = !!product.existingProductNotificationId;

  const addNotificationMutation = useMutation(
    async () => {
      return request({
        url: `/${countryLang}/customer/product-notification/add/${product.productID}`,
        method: "post",
        session: session
      });
    },
    {
      onSuccess: async () => {
        // Invalidate the product query to refresh the UI
        await queryClient.invalidateQueries(["product", product.productID, countryLang]);
      },

      onSettled: () => {
        setNotificationLoading(false);
      }
    }
  );

  const deleteNotificationMutation = useMutation(
    async () => {
      return request({
        url: `/${countryLang}/customer/product-notification/delete/${product.existingProductNotificationId}`,
        method: "delete",
        session: session
      });
    },
    {
      onSuccess: async () => {
        // Invalidate the product query to refresh the UI
        await queryClient.invalidateQueries(["product", product.productID, countryLang]);
      },
      onSettled: () => {
        setNotificationLoading(false);
      }
    }
  );

  const handleNotificationClick = () => {
    if (notificationLoading) return;

    setNotificationLoading(true);
    if (hasExistingNotification) {
      deleteNotificationMutation.mutate();
    } else {
      addNotificationMutation.mutate();
    }
  }

  const getShippingTextColor = (color: string | undefined) => {
    switch(color) {
      case "green":
        return vars.textGreen;
      case "orange":
        return vars.textOrange;
      case "info":
        return vars.backgroundBlue;
      case "red":
        return vars.secondaryFontColor;
      case "neutral":
        return vars.secondaryFontColor;
      default:
        return color;
    }
  }

  const getNormalizedValue = () => {
    if (product.normalizedUnit && product.normalizedPrice) {
      return (
        <NormalizedUnitPrice price={product.normalizedPrice} unit={product.normalizedUnit} />
      )
    }
  }

  const showShippingCosts: boolean = !product.isFreeShipping && product.shippingCost!=null && product.shippingCost.length>0;

  return (
    <CtaContain id="product-price-section">
      <div className="price">
        <div className="fs-3 fw-bold d-flex justify-content-center align-center">
          {product.currencyPrice} {getNormalizedValue()}
          <Tag productTarget={product}
               className={"card-tag"}
               type="product"/>
        </div>
        {!product.isPriceOnDemand &&
          <div className="old-price">
            <span className="angled-strikethrough">{product.currencyUvp}</span>
          </div>
        }
        <div>
          <span> { product.specialDealText } </span>
        </div>

        {product.margin &&
          <InternalPriceMargin className={"internal-margin"}
                               absMargin={product.margin.absMargin}
                               percentage={product.margin.percentage}
                               color={product.marginColor}
          />
        }
      </div>
      <div className={"stock-status"}>
        {product && product.stockDisplayText &&
          <Badge text={parse(product.stockDisplayText?.replace(/(<([^>]+)>)/gi, ""))}
                 type={getMappedStockColor(product.stockDisplayColor)} size="medium"
                 background={false}/>}
        {product.stockDisplayTooltip &&
          <CustomTooltip placement={"left"}
                         content={product.stockDisplayTooltip}
          />
        }
      </div>

      {(product && isUserLoggedIn && (product.displayProductNotificationButton || hasExistingNotification)) &&
        <div className="product-notification">
          <Button
            variant={"secondary"}
            onClick={handleNotificationClick}
            disabled={notificationLoading}
          >
            <Icon variant={"xSmall"} className={"notification-icon"}>bell</Icon>
            {notificationLoading ?
              <Spinner animation="border" size="sm"/> :
              hasExistingNotification ?
                (messagesQuery.data.messages["remove-product-notification"]) :
                messagesQuery.data.messages["my-product-notifications-button"]
            }
            <CustomTooltip content={messagesQuery.data.messages["my-product-notifications-link"]}
                           placement={"bottom"}/>
          </Button>
        </div>
      }

      {messagesQuery.data && product && product.productNotificationButtonDisabled && !isUserLoggedIn && (product?.shippingText?.cssColor == "red") &&
        <div className="login-for-notification">
          {messagesQuery.data.messages["product-notification-login"]}
        </div>
      }

      {product && product.isOnlineOnly && messagesQuery.data &&
        <div className={"online-only"}>
          <Badge text={messagesQuery.data.messages["online-only"]}
                 type={getMappedStockColor(product.stockDisplayColor)} size="medium"
                 background={true}/>
        </div>
      }

      {product.stockDisplayShippingText &&
        <div className="shipping-text">
          {product.stockDisplayShippingText}
        </div>
      }
      <ShippingCosts>
        {showShippingCosts && <div className="mt-2">
          {messagesQuery.data && <span className="shipping-cost">{messagesQuery.data.messages["shipping-cost"]}: {product.shippingCost}</span>}
        </div>}

        {!showShippingCosts && product.isDeliveryPriceOnDemand && messagesQuery.data &&
          <span> { messagesQuery.data.messages["delivery-price-on-demand"] } </span>
        }

        {product.shippingText?.text &&
          <div className="mt-1"
               style={{color: getShippingTextColor(product.shippingText.cssColor)}}>
            {product.shippingText.text}
          </div>
        }
      </ShippingCosts>

      {props.productInCart && messagesQuery.data &&
        <div className={"product-in-cart"}>
          {messagesQuery.data.messages["product-is-in-cart"]}
          { !product.isReturnable &&
            <div className="not-returnable">
              ( { messagesQuery.data.messages["product-no-return"] } )
            </div>
          }
        </div>
      }

      {messagesQuery.data && !product.canBeOrdered &&
        <div className={"not-orderable text-muted mt-3"}>
          {messagesQuery.data.messages["product-cannot-be-ordered-online"]}
        </div>
      }

      {product.canBeOrdered && 
        <AddToCart 
          variant={"primary"}
          layout={"default"}
          productId={product.productID}
          currency={product.currency}
          name={product.productName}
          price={product.price}
          gaCId={clientCtx?.gaCId || ''}
          width="100%"
          className="mt-4"
          buttonMessage={messagesQuery.data?.messages["add-to-cart"]}
        />
      }

      <div className="d-flex justify-content-between">
        <AddToWishlist 
          layout={"default"} 
          variant={"secondary"} 
          productId={product.productID} 
          width="48%" 
          className="mt-2"
        />
        <Button 
          onClick={handleShowShare}
          variant={"secondary"} 
          width={"48%"} 
          className={"mt-2 ml-auto"}
        >
          {!showShare && <Icon variant={"medium"} className={"d-inline-block"}>share-line</Icon>}
          {showShare && <Icon variant={"medium"} className={"d-inline-block"}>share-fill</Icon>}
        </Button>
      </div>
      {showShare &&
        <div className="share">
          <SocialShare />
        </div>
      }
    </CtaContain>
  );
}

export default ProductCTA;
